import { memo, useCallback, useMemo } from "react"

import usePermissions from "api/usePermissions"
import { useUserDetails } from "api/useUserDetails"
import { permissionsUtil } from "utils/permissionsUtil"
import { getSelectProjectOptions } from "utils/projectsUtil"

import { FilterPopover } from "components/FilterComponents/FilterPopover/FilterPopover"
import { FilterType } from "components/FilterComponents/FilterPopover/types"

interface ProjectFilterProps {
    selectedProjects: string[]
    setSelectedProjects: (projects: string[]) => void
    dataTestId?: string
}

const ALL_PROJECTS_VALUE = '__ALL__';

export const ProjectFilter = memo(({ selectedProjects, setSelectedProjects, dataTestId }: ProjectFilterProps) => {
    const { getPermissions } = usePermissions();
    const permissions = getPermissions();
    const allowedPublishers = useMemo(() => permissionsUtil.getAccessPublisherOrders(permissions), [permissions]);

    const { userDetails } = useUserDetails();
    const projectsDetails = useMemo(() => {
        return userDetails?.projects
    }, [userDetails])

    const projectOptions = useMemo(() => {
        const options = projectsDetails && allowedPublishers.length > 0 ? getSelectProjectOptions(projectsDetails, allowedPublishers).map(project => ({
            label: project.content,
            value: project.value
        }))
            : []

        // Add "All" option at the beginning
        return [
            { label: 'All', value: ALL_PROJECTS_VALUE },
            ...options
        ]
    }, [projectsDetails, allowedPublishers]);

    // Get all project values (excluding "All")
    const allProjectValues = useMemo(() => {
        return projectOptions.filter(opt => opt.value !== ALL_PROJECTS_VALUE).map(opt => opt.value)
    }, [projectOptions])

    // Check if all projects are selected
    const areAllProjectsSelected = useMemo(() => {
        return allProjectValues.length > 0 && allProjectValues.every(val => selectedProjects.includes(val))
    }, [allProjectValues, selectedProjects])

    // Add "All" to selectedItems if all projects are selected
    const displayedSelectedProjects = useMemo(() => {
        if (areAllProjectsSelected) {
            return [ALL_PROJECTS_VALUE, ...selectedProjects]
        }
        return selectedProjects
    }, [areAllProjectsSelected, selectedProjects])

    const handleProjectsSelectionChange = useCallback((items: string[]) => {
        if (items.includes(ALL_PROJECTS_VALUE)) {
            setSelectedProjects([...allProjectValues])
            return
        }
        setSelectedProjects(items.filter((value) => value !== ALL_PROJECTS_VALUE))
    }, [allProjectValues, setSelectedProjects]);

    return (
        <FilterPopover
            label="Project"
            options={projectOptions}
            selectedItems={displayedSelectedProjects}
            selectionCount={selectedProjects.length}
            onSelectionChange={handleProjectsSelectionChange}
            filterType={FilterType.AUTOCOMPLETE_CHECKLIST}
            placeholder="Search projects..."
            dataTestId={dataTestId}
        />
    );
});

ProjectFilter.displayName = 'ProjectFilter';